import {getPublicLoginPath, isSelectiveAiDomains} from 'common/HelperFunctions'
import Spinner from 'common/components/Spinner'
import {calculateErrorMessage} from 'common/helpers'
import {isSupportSessionToken} from 'common/helpers/supportSessionToken'
import {resolveCanonicalDomain} from 'common/helpers/universalLoginHandoff'
import {FC, useEffect, useRef} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useNavigate, useSearchParams} from 'react-router'
import {toast} from 'react-toastify'
import {RootState} from 'setup'
import {
  REDUX_SAVE_LOGIN_INFO,
  REDUX_SET_ACCESS_TOKEN,
} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {loginWithJWT} from 'setup/redux/Actions/SignIn/login'

import {
  clearMustCreatePassword,
  loginResultRequiresPasswordChange,
  resolveSelectiveAiHandoffDestination,
  setMustCreatePassword,
} from '../selective/mustCreatePasswordHelpers'

function resolveAuthHandoffPath(pathParam: string | null): string | null {
  if (!pathParam) return null
  let path = pathParam
  if (!path.startsWith('/')) {
    path = `/${path}`
  }
  if (path.startsWith('/school')) {
    path = path.replace('/school', '/institute')
  }
  return path
}

export const Auth: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const jwtStarted = useRef(false)

  const accessToken = useSelector<RootState>(
    (state) => state.userReducer.accessToken,
    shallowEqual
  ) as string

  const isLoggedIn = useSelector<RootState>(
    (state) => state.userReducer.isLoggedIn,
    shallowEqual
  ) as boolean

  const mustCreatePassword = useSelector<RootState>(
    (state) => (state.userReducer as any).mustCreatePassword,
    shallowEqual
  ) as boolean

  const authToken = searchParams.get('auth_token')
  const handoffPath = resolveAuthHandoffPath(searchParams.get('path'))

  useEffect(() => {
    if (authToken) {
      // Active session: skip re-exchanging a one-time invite/handoff token (issue 10).
      // Re-exchange when mustCreatePassword is set (e.g. after forgot/set password handoff).
      // A support/impersonation token is always exchanged: it must replace the session
      // already persisted on this origin, otherwise the operator lands on that old session
      // and the support banner never shows.
      if (
        isLoggedIn &&
        accessToken &&
        isSelectiveAiDomains() &&
        !mustCreatePassword &&
        !isSupportSessionToken(authToken)
      ) {
        navigate(handoffPath || '/dashboard?step=0', {replace: true})
        return
      }

      if (jwtStarted.current) return
      jwtStarted.current = true

      // A stale token can still be sitting in this origin's persisted store
      // (e.g. an earlier abandoned create-password visit) and may already be
      // invalidated server-side (token-version bump on password reset).
      // Drop it now, synchronously, before the exchange below resolves —
      // otherwise background features (socket, chatbot) can fire an
      // authenticated call with it, 401, and trip the global forced /logout
      // redirect mid-exchange, which then repeats forever since the stale
      // token is still there on reload.
      if (accessToken) {
        dispatch({type: REDUX_SET_ACCESS_TOKEN, payload: null})
      }

      const timeout = setTimeout(() => {
        navigate(getPublicLoginPath(), {replace: true})
      }, 1000 * 30)

      const canonicalDomain = resolveCanonicalDomain(searchParams)

      const onSuccess = (res: any) => {
        clearTimeout(timeout)

        if (loginResultRequiresPasswordChange(res?.result)) {
          setMustCreatePassword(dispatch)
          navigate('/create-password', {replace: true})
          return
        }

        clearMustCreatePassword(dispatch)

        dispatch({
          type: REDUX_SAVE_LOGIN_INFO,
          payload: {
            result: {
              ...res.result,
              promptChangePassword: false,
            },
          },
        })

        if (handoffPath) {
          const destination = resolveSelectiveAiHandoffDestination(handoffPath)
          navigate(
            {
              pathname: destination.pathname,
              search: destination.search || '?step=0',
            },
            {replace: true}
          )
        } else if (isSelectiveAiDomains()) {
          navigate('/dashboard?step=0', {replace: true})
        } else {
          navigate('/', {replace: true})
        }
      }

      const onFailure = (err: any) => {
        clearTimeout(timeout)
        jwtStarted.current = false
        const message = calculateErrorMessage(
          err,
          isSelectiveAiDomains()
            ? 'This link has expired. Please sign in with your credentials.'
            : 'Invalid Auth Token'
        )
        if (!isSelectiveAiDomains() || err?.statusCode === 401) {
          toast.error(message)
        }
        navigate(getPublicLoginPath(), {replace: true})
      }

      dispatch(loginWithJWT(authToken, onSuccess, onFailure, canonicalDomain))
      return
    }

    if (accessToken) {
      navigate(handoffPath || '/', {replace: true})
    } else {
      navigate(getPublicLoginPath(), {replace: true})
    }
  }, [
    accessToken,
    authToken,
    dispatch,
    handoffPath,
    isLoggedIn,
    mustCreatePassword,
    navigate,
    searchParams,
  ])

  return (
    <div className='d-flex flex-column align-items-center justify-content-center'>
      <Spinner type='grow' size='lg' color='primary' />
    </div>
  )
}
